import {
    Button,
    Card,
    Checkbox,
    Form,
    FormInstance,
    Input,
    Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import datetime from '@/infra/adapters/datetime';
import { ResultCode } from '@/infra/adapters/models';
import { deserialize } from '@/infra/adapters/transformer';
import {
    AuthenticationError,
    InvalidParamError,
    NotFoundError,
} from '@/infra/exceptions';
import { LoginAttemptResource } from '@/v1/main/adapters/models';
import { AuthService } from '@/v1/main/domain/auth';
import { LogoImage } from '@/v1/main/views/Image';
import {
    MessageOption,
    MessageType,
    useAuth,
    useError,
    useMessage,
} from '@/v1/main/views/Provider';

import styles from './Form.module.scss';

interface Props {
    usecase: AuthService;
}

interface FormProps {
    account: string;
    password: string;
    remember: boolean;
}

export const LoginForm: React.FC<Props> = ({ usecase }: Props) => {
    const { t } = useTranslation(['interface', 'title', 'field']);

    const { setLoggedIn } = useAuth();
    const { setMessageOption } = useMessage();
    const { onRequestFail } = useError();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        const onInit = async () => {
            const rememberedAccount = await usecase.getAccount();
            if (rememberedAccount != '') {
                form.setFieldsValue({
                    account: rememberedAccount,
                    remember: true,
                });
            }
        };
        onInit();
    }, [form]);

    const onFinish = async () => {
        console.log('onFinish');
        setLoading(true);
        try {
            await form.validateFields();

            const { account, password, remember }: FormProps =
                form.getFieldsValue();
            await usecase.login(account, password);
            if (remember) {
                usecase.rememberAccount(account);
            } else {
                usecase.clearRememberedAccount();
            }
            setLoggedIn(true);
        } catch (err: unknown) {
            console.error('err', err);
            await onFinishFail(err);
        }
        setLoading(false);
    };

    const onFinishFail = async (err: unknown) => {
        console.trace(err);

        const isFormValid = form
            .getFieldsError()
            .every((fieldErr) => fieldErr.errors.length === 0);
        if (!isFormValid) {
            return;
        }

        try {
            await onRequestFail(err, form);
        } catch (err) {
            await onFail(err, form);
        }
    };

    const onFail = async (err: unknown, form: FormInstance) => {
        const fields = [];
        if (err instanceof InvalidParamError) {
            if (err.code == ResultCode.INVALID_ACCOUNT) {
                fields.push({
                    name: 'account',
                    errors: [
                        t('validator.invalid', {
                            ns: 'interface',
                            field: t('account', { ns: 'field' }),
                        }),
                    ],
                });
            } else if (err.code == ResultCode.INVALID_PASSWORD) {
                fields.push({
                    name: 'password',
                    errors: [
                        t('validator.invalid', {
                            ns: 'interface',
                            field: t('password', { ns: 'field' }),
                        }),
                    ],
                });
            }
        } else if (err instanceof NotFoundError) {
            fields.push({
                name: 'account',
                errors: [
                    t('validator.not_found', {
                        ns: 'interface',
                        field: t('account', { ns: 'field' }),
                    }),
                ],
            });
        } else if (err instanceof AuthenticationError) {
            const result = deserialize(LoginAttemptResource, err.result);
            if (err.code == ResultCode.INVALID_PASSWORD) {
                fields.push({
                    name: 'password',
                    errors: [
                        t('validator.incorrect', {
                            ns: 'interface',
                            field: t('password', { ns: 'field' }),
                        }),
                    ],
                });
                if (result.remainingCount > 0) {
                    const opt: MessageOption = {
                        type: MessageType.ERROR,
                        content: t('message.login_attempt', {
                            ns: 'interface',
                            remaining_count: result.remainingCount,
                        }),
                        duration: 10,
                    };
                    setMessageOption(opt);
                } else {
                    const diff = datetime.to(result.unlockTime);
                    const opt: MessageOption = {
                        type: MessageType.ERROR,
                        content: t('message.account_lockout', {
                            ns: 'interface',
                            countdown: diff,
                            formatParams: {
                                countdown: {
                                    hour: 'numeric',
                                    minute: 'numeric',
                                    second: 'numeric',
                                    hourCycle: 'h23',
                                    timeZone: 'UTC',
                                },
                            },
                        }),
                        duration: 10,
                    };
                    setMessageOption(opt);
                }
            }
        }
        if (fields.length > 0) {
            form.setFields(fields);
            return Promise.resolve();
        }
        const opt: MessageOption = {
            type: MessageType.ERROR,
            content: t('message.action_failed', {
                ns: 'interface',
                tip: '',
            }),
        };
        setMessageOption(opt);
        return Promise.resolve();
    };

    const action = (
        <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            onClick={onFinish}
            block
        >
            {t('action.login', { ns: 'interface' })}
        </Button>
    );

    const cover = (
        <div>
            <LogoImage />
            <Typography.Title
                level={4}
                style={{ display: 'flex', justifyContent: 'center' }}
            >
                公車多元支付驗票機監控維運平台
            </Typography.Title>
        </div>
    );

    return (
        <Card
            className={styles.login}
            bordered={false}
            cover={cover}
            actions={[action]}
        >
            <Form
                name="login"
                layout="vertical"
                form={form}
                requiredMark={false}
                scrollToFirstError={true}
                validateTrigger="onSubmit"
            >
                <Form.Item
                    name="account"
                    label={t('account', { ns: 'field' })}
                    validateFirst
                    rules={[
                        {
                            required: true,
                            message: t<string>('validator.required', {
                                ns: 'interface',
                                field: '${label}',
                            }),
                        },
                        {
                            pattern: new RegExp(/^(?=\S).*(?:[^\s])$/, 'g'),
                            message: t<string>('validator.whitespace', {
                                ns: 'interface',
                            }),
                        },
                        {
                            type: 'email',
                            message: t<string>('validator.invalid', {
                                ns: 'interface',
                                field: '${label}',
                            }),
                        },
                    ]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                    name="password"
                    label={t('password', { ns: 'field' })}
                    validateFirst
                    rules={[
                        {
                            required: true,
                            message: t<string>('validator.required', {
                                ns: 'interface',
                                field: '${label}',
                            }),
                        },
                        {
                            pattern: new RegExp(/^(?=\S).*(?:[^\s])$/, 'g'),
                            message: t<string>('validator.whitespace', {
                                ns: 'interface',
                            }),
                        },
                        {
                            pattern: new RegExp(
                                '^[a-zA-Z0-9\u0040\u0024\u0023\u0025\u005E\u0026\u002A]{8,32}$', // @$#%^&*
                                'g'
                            ),
                            message: t<string>('validator.password_invalid', {
                                ns: 'interface',
                                field: '${label}',
                            }),
                        },
                    ]}
                >
                    <Input.Password />
                </Form.Item>
                <Form.Item name="remember" valuePropName="checked">
                    <Checkbox>{t('remember', { ns: 'field' })}</Checkbox>
                </Form.Item>
            </Form>
        </Card>
    );
};
